/*
 **	OMNI_IQ Mobile (c) 2025 Silver Falls Capital. All Rights Reserved
 **
 **	Global Data Store - Zustand
 **	Centralized state management for devices, locations, and device data
 **	Prevents redundant API calls and 429 rate limit errors
 */

import { create } from 'zustand'
import { getAllDevices, getAllLocations, getDeviceData, getDeviceById, type Device, type Location, type DeviceData } from '../services/api'

interface DataState {
  // Data
  devices: Device[]
  locations: Location[]
  deviceData: Record<string, DeviceData[]>

  // New device tracking
  newDeviceIds: Set<string>
  newDeviceTimestamps: Map<string, number>

  // Loading states
  isLoadingDevices: boolean
  isLoadingLocations: boolean
  isLoadingDeviceData: boolean

  // Error states
  devicesError: string | null
  locationsError: string | null
  deviceDataError: string | null

  // Cache timestamps (to determine if data is stale)
  lastDevicesFetch: number | null
  lastLocationsFetch: number | null
  lastDeviceDataFetch: Record<string, number>

  // Actions
  fetchDevices: (token: string, forceRefresh?: boolean) => Promise<void>
  fetchSingleDevice: (deviceId: string, token: string) => Promise<void>
  fetchLocations: (token: string, forceRefresh?: boolean) => Promise<void>
  fetchDeviceData: (deviceId: string, token: string, forceRefresh?: boolean) => Promise<void>
  fetchAllDeviceData: (token: string, forceRefresh?: boolean) => Promise<void>
  fetchAllData: (token: string, forceRefresh?: boolean) => Promise<void>
  updateRealtimeDeviceData: (deviceId: string, data: Partial<DeviceData>) => void
  markDeviceAsNew: (deviceId: string) => void
  isDeviceNew: (deviceId: string) => boolean
  cleanupExpiredNewDevices: () => void
  clearCache: () => void
  reset: () => void
}

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000

// Helper to check if cache is still valid
const isCacheValid = (lastFetch: number | null): boolean => {
  if (!lastFetch) return false
  return Date.now() - lastFetch < CACHE_DURATION
}

export const useDataStore = create<DataState>((set: any, get: any) => ({
  // Initial state
  devices: [],
  locations: [],
  deviceData: {},

  newDeviceIds: new Set<string>(),
  newDeviceTimestamps: new Map<string, number>(),

  isLoadingDevices: false,
  isLoadingLocations: false,
  isLoadingDeviceData: false,

  devicesError: null,
  locationsError: null,
  deviceDataError: null,

  lastDevicesFetch: null,
  lastLocationsFetch: null,
  lastDeviceDataFetch: {},

  // Fetch devices with caching
  fetchDevices: async (token: string, forceRefresh = false) => {
    const { lastDevicesFetch, isLoadingDevices } = get()

    // Skip if already loading
    if (isLoadingDevices) {
      console.log('📦 Devices fetch already in progress, skipping...')
      return
    }

    // Skip if cache is valid and not forcing refresh
    if (!forceRefresh && isCacheValid(lastDevicesFetch)) {
      console.log('📦 Using cached devices data')
      return
    }

    console.log('📦 Fetching devices from API...')
    set({ isLoadingDevices: true, devicesError: null })

    try {
      const devices = await getAllDevices(token)
      set({
        devices,
        lastDevicesFetch: Date.now(),
        devicesError: null
      })
      console.log(`📦 Fetched ${devices.length} devices`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch devices'
      console.error('📦 Error fetching devices:', error)
      set({ devicesError: errorMessage })
    } finally {
      set({ isLoadingDevices: false })
    }
  },

  // Fetch locations with caching
  fetchLocations: async (token: string, forceRefresh = false) => {
    const { lastLocationsFetch, isLoadingLocations } = get()

    // Skip if already loading
    if (isLoadingLocations) {
      console.log('📍 Locations fetch already in progress, skipping...')
      return
    }

    // Skip if cache is valid and not forcing refresh
    if (!forceRefresh && isCacheValid(lastLocationsFetch)) {
      console.log('📍 Using cached locations data')
      return
    }

    console.log('📍 Fetching locations from API...')
    set({ isLoadingLocations: true, locationsError: null })

    try {
      const locations = await getAllLocations(token)
      set({
        locations,
        lastLocationsFetch: Date.now(),
        locationsError: null
      })
      console.log(`📍 Fetched ${locations.length} locations`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch locations'
      console.error('📍 Error fetching locations:', error)
      set({ locationsError: errorMessage })
    } finally {
      set({ isLoadingLocations: false })
    }
  },

  // Fetch device data for a single device with caching
  fetchDeviceData: async (deviceId: string, token: string, forceRefresh = false) => {
    const { lastDeviceDataFetch, isLoadingDeviceData } = get()

    // Skip if already loading
    if (isLoadingDeviceData) {
      console.log(`📊 Device data fetch already in progress, skipping ${deviceId}...`)
      return
    }

    // Skip if cache is valid for this device and not forcing refresh
    if (!forceRefresh && isCacheValid(lastDeviceDataFetch[deviceId])) {
      console.log(`📊 Using cached device data for ${deviceId}`)
      return
    }

    console.log(`📊 Fetching device data for ${deviceId}...`)
    set({ isLoadingDeviceData: true, deviceDataError: null })

    try {
      const data = await getDeviceData(deviceId, token, undefined, true)
      set((state: any) => ({
        deviceData: {
          ...state.deviceData,
          [deviceId]: data
        },
        lastDeviceDataFetch: {
          ...state.lastDeviceDataFetch,
          [deviceId]: Date.now()
        },
        deviceDataError: null
      }))
      console.log(`📊 Fetched data for device ${deviceId}`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : `Failed to fetch data for device ${deviceId}`
      console.error(`📊 Error fetching device data for ${deviceId}:`, error)
      set({ deviceDataError: errorMessage })
    } finally {
      set({ isLoadingDeviceData: false })
    }
  },

  // Fetch device data for all devices
  fetchAllDeviceData: async (token: string, forceRefresh = false) => {
    const { devices, isLoadingDeviceData } = get()

    if (isLoadingDeviceData) {
      console.log('📊 Device data fetch already in progress, skipping all...')
      return
    }

    console.log(`📊 Fetching device data for ${devices.length} devices...`)
    set({ isLoadingDeviceData: true, deviceDataError: null })

    try {
      // Fetch data for all devices with rate limiting (5 at a time to avoid 429)
      const BATCH_SIZE = 5
      const results: Array<{ deviceId: string; data: any } | null> = []

      for (let i = 0; i < devices.length; i += BATCH_SIZE) {
        const batch = devices.slice(i, i + BATCH_SIZE)
        console.log(`📊 Fetching batch ${Math.floor(i / BATCH_SIZE) + 1}/${Math.ceil(devices.length / BATCH_SIZE)}`)

        const batchPromises = batch.map(async (device: any) => {
          const { lastDeviceDataFetch } = get()

          // Skip if cache is valid for this device and not forcing refresh
          if (!forceRefresh && isCacheValid(lastDeviceDataFetch[device.deviceId])) {
            console.log(`📊 Using cached device data for ${device.deviceId}`)
            return null
          }

          try {
            const data = await getDeviceData(device.deviceId, token, undefined, true)
            return { deviceId: device.deviceId, data }
          } catch (error) {
            console.error(`📊 Error fetching data for device ${device.deviceId}:`, error)
            return null
          }
        })

        const batchResults = await Promise.all(batchPromises)
        results.push(...batchResults)

        // Small delay between batches to avoid rate limiting
        if (i + BATCH_SIZE < devices.length) {
          await new Promise(resolve => setTimeout(resolve, 100))
        }
      }

      // Update state with all fetched data
      set((state: any) => {
        const newDeviceData = { ...state.deviceData }
        const newLastFetch = { ...state.lastDeviceDataFetch }

        results.forEach(result => {
          if (result) {
            newDeviceData[result.deviceId] = result.data
            newLastFetch[result.deviceId] = Date.now()
          }
        })

        return {
          deviceData: newDeviceData,
          lastDeviceDataFetch: newLastFetch,
          deviceDataError: null
        }
      })

      console.log('📊 Fetched all device data')
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch device data'
      console.error('📊 Error fetching all device data:', error)
      set({ deviceDataError: errorMessage })
    } finally {
      set({ isLoadingDeviceData: false })
    }
  },

  // Fetch all data (devices, locations, and device data)
  fetchAllData: async (token: string, forceRefresh = false) => {
    console.log('📦 Fetching all data...')

    // Fetch devices and locations in parallel
    await Promise.all([
      get().fetchDevices(token, forceRefresh),
      get().fetchLocations(token, forceRefresh)
    ])

    // Then fetch device data
    await get().fetchAllDeviceData(token, forceRefresh)

    console.log('📦 All data fetched')
  },

  // Fetch single device and merge into devices array
  fetchSingleDevice: async (deviceId: string, token: string) => {
    try {
      console.log('📱 Fetching single device:', deviceId)
      const device = await getDeviceById(deviceId, token)

      set((state: any) => {
        // Check if device already exists
        const existingIndex = state.devices.findIndex((d: any) => d.deviceId === deviceId)

        let updatedDevices
        if (existingIndex >= 0) {
          // Update existing device
          updatedDevices = [...state.devices]
          updatedDevices[existingIndex] = device
        } else {
          // Add new device to list
          updatedDevices = [device, ...state.devices]
        }

        return { devices: updatedDevices }
      })

      // Always mark as new when fetched (since this is only called after registration/update)
      get().markDeviceAsNew(deviceId)

      console.log('✅ Single device fetched and merged, marked as new')
    } catch (error) {
      console.error('❌ Failed to fetch single device:', error)
    }
  },

  // Mark device as new with timestamp
  markDeviceAsNew: (deviceId: string) => {
    set((state: any) => {
      const newIds = new Set(state.newDeviceIds)
      const newTimestamps = new Map(state.newDeviceTimestamps)

      newIds.add(deviceId)
      newTimestamps.set(deviceId, Date.now())

      return {
        newDeviceIds: newIds,
        newDeviceTimestamps: newTimestamps
      }
    })
  },

  // Check if device is still "new" (within 60 seconds)
  isDeviceNew: (deviceId: string): boolean => {
    const { newDeviceIds, newDeviceTimestamps } = get()

    if (!newDeviceIds.has(deviceId)) return false

    const timestamp = newDeviceTimestamps.get(deviceId)
    if (!timestamp) return false

    const elapsed = Date.now() - timestamp
    return elapsed < 60000 // 60 seconds
  },

  // Remove devices that are no longer "new"
  cleanupExpiredNewDevices: () => {
    set((state: any) => {
      const newIds = new Set(state.newDeviceIds)
      const newTimestamps = new Map(state.newDeviceTimestamps)
      const now = Date.now()

      // Check each device and remove if expired
      for (const deviceId of newIds) {
        const timestamp = newTimestamps.get(deviceId)
        if (timestamp && typeof timestamp === 'number' && now - timestamp >= 60000) {
          newIds.delete(deviceId)
          newTimestamps.delete(deviceId)
        }
      }

      return {
        newDeviceIds: newIds,
        newDeviceTimestamps: newTimestamps
      }
    })
  },

  // Update device data with realtime WebSocket data
  updateRealtimeDeviceData: (deviceId: string, data: Partial<DeviceData>) => {
    set((state: any) => {
      const existingData = state.deviceData[deviceId] || []
      const currentTimestamp = new Date().toISOString()

      // Create a new device data entry with current timestamp
      const newDataPoint: DeviceData = {
        deviceId,
        timestamp: currentTimestamp,
        dp0: data.dp0,
        dp1: data.dp1,
        dp0wt: data.dp0wt,
        dp0at: data.dp0at,
        dp1wt: data.dp1wt,
        dp1at: data.dp1at,
        dp0_alarm_state: data.dp0_alarm_state,
        dp1_alarm_state: data.dp1_alarm_state,
        w_rssi: data.w_rssi,
        c_rssi: data.c_rssi,
        measure_unit: data.measure_unit,
        br_ping_ct: data.br_ping_ct,
        br_bad_ping_ct: data.br_bad_ping_ct,
        br_bad_ping_seq_ct: data.br_bad_ping_seq_ct,
        br_longest_bad_ping_seq_ct: data.br_longest_bad_ping_seq_ct
      }

      // Keep latest data at the front (most recent first)
      // Keep last 10 data points to maintain some history
      const updatedData = [newDataPoint, ...existingData].slice(0, 10)

      // Also update the device's lastSeen timestamp in the devices array
      const updatedDevices = state.devices.map((device: any) =>
        device.deviceId === deviceId
          ? { ...device, lastSeen: currentTimestamp }
          : device
      )

      return {
        devices: updatedDevices,
        deviceData: {
          ...state.deviceData,
          [deviceId]: updatedData
        }
      }
    })
  },

  // Clear all cached data and timestamps
  clearCache: () => {
    console.log('🗑️ Clearing data cache...')
    set({
      lastDevicesFetch: null,
      lastLocationsFetch: null,
      lastDeviceDataFetch: {}
    })
  },

  // Reset entire store to initial state
  reset: () => {
    console.log('🗑️ Resetting data store...')
    set({
      devices: [],
      locations: [],
      deviceData: {},
      newDeviceIds: new Set<string>(),
      newDeviceTimestamps: new Map<string, number>(),
      isLoadingDevices: false,
      isLoadingLocations: false,
      isLoadingDeviceData: false,
      devicesError: null,
      locationsError: null,
      deviceDataError: null,
      lastDevicesFetch: null,
      lastLocationsFetch: null,
      lastDeviceDataFetch: {}
    })
  }
}))
